import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCcw, X } from "lucide-react";
import { useAtom } from "jotai";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";

import { activeChildDetail, ChatAtom } from "../../atomstore/store";
import { fetchChat, SendMessage } from "../../queryfunctions/queryfunctions";
import SpinnerLoading from "./SpinnerLoading";
import { useRequestProcessor } from "../../requestProcessor";
import { useNavigate } from "react-router-dom";

const Chat = () => {
    const [msg, setMsg] = useState("");
    const [startIndex, setStartIndex] = useState(0);
    const [moreData, setMoreData] = useState(true);
    const [loading, setLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null); // ✅ new
    const navigate = useNavigate();
    const [chat, setChat] = useAtom(ChatAtom);
    const [activeChild] = useAtom(activeChildDetail);

    const { useQueryWrapper, useMutationWrapper } = useRequestProcessor();

    // Fetch chat messages
    const { isFetching } = useQueryWrapper(
        ["Chat", "chat" + activeChild?.id, startIndex],
        async () => fetchChat(startIndex, setMoreData, chat, setChat),
        { keepPreviousData: true }
    );

    useMemo(() => {
        console.log(`[Chat] Fetching Conversations for child: ${activeChild?.id}`);
    }, [activeChild]);

    const updateState = (msgText) => {
        const newItem = {
            message: msgText.trim(),
            who: "parent",
            time: moment().format("hh:mm A"),
        };

        const today = moment().format("DD-MM-YYYY");
        // const updatedChat = [...chat];
        const updatedChat = Array.isArray(chat) ? [...chat] : [];
        const existingDateGroupIndex = updatedChat.findIndex(
            (item) => item.dateGroup === today
        );

        if (existingDateGroupIndex !== -1) {
            updatedChat[existingDateGroupIndex].data.push(newItem);
            setChat(updatedChat);
        } else {
            // setChat([{ dateGroup: today, data: [newItem] }, ...chat]);
            setChat([
                { dateGroup: today, data: [newItem] },
                ...(Array.isArray(chat) ? chat : []),
            ]);
        }
    };

    const send = useMutationWrapper("SendMessage", () => SendMessage(msg, updateState));

    const fetchMoreData = () => {
        const nextIndex = startIndex + 50 + 1;
        setStartIndex(nextIndex);
    };

    const handleRefresh = () => {
        setLoading(true);
        fetchChat(0, setMoreData, chat, setChat).finally(() => setLoading(false));
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <div className="flex-1 cursor-pointer" onClick={() => navigate(-1)}>
                    <h2 className="text-lg font-bold text-gray-800">{activeChild?.["admission_center_name.name"]}</h2>
                    <p className="text-sm text-gray-500">Usually replies within an hour</p>
                </div>

                <button
                    onClick={handleRefresh}
                    className="flex items-center gap-2 bg-cyan-500 text-white px-3 py-1.5 rounded-full"
                >
                    <RefreshCcw size={14} />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Spinner when loading */}
            {(isFetching || loading || send.isLoading) && <SpinnerLoading />}

            {/* Chat messages */}
            <div
                id="chat-container"
                className="flex-1 overflow-y-auto p-4 space-y-3 flex flex-col-reverse"
            >
                <InfiniteScroll
                    dataLength={chat?.length || 0}
                    next={fetchMoreData}
                    inverse={true}
                    hasMore={moreData}
                    scrollableTarget="chat-container"
                    style={{ display: "flex", flexDirection: "column-reverse" }}
                >
                    {Array.isArray(chat) &&
                        chat.map((dateGroup, i) => (
                            <div key={i}>
                                {/* Date label */}
                                <div className="text-center mb-2">
                                    <span className="bg-white text-gray-600 text-xs px-3 py-1 rounded-full shadow">
                                        {moment(dateGroup.dateGroup, "DD-MM-YYYY").format("D MMM YYYY")}
                                    </span>
                                </div>

                                {dateGroup.data.map((msg, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex ${msg.who === "parent" ? "justify-end" : "justify-start"}`}
                                    >
                                        <div
                                            className={`max-w-[100%] mb-2 rounded-2xl p-3 break-words ${msg.who === "parent"
                                                ? "bg-cyan-500 text-white"
                                                : "bg-gray-100 text-gray-800"
                                                }`}
                                        >
                                            {/* ✅ Show image or text */}
                                            {msg.message.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                                <img
                                                    src={msg.message}
                                                    alt="chat"
                                                    onClick={() => setSelectedImage(msg.message)} // ✅ open image
                                                    className="rounded-lg max-w-44 h-auto cursor-pointer hover:opacity-90 transition"
                                                />
                                            ) : (
                                                <p className="text-sm">{msg.message}</p>
                                            )}

                                            <p
                                                className={`text-xs mt-1 ${msg.who === "parent"
                                                    ? "text-cyan-100"
                                                    : "text-gray-500"
                                                    }`}
                                            >
                                                {msg.time}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                </InfiniteScroll>

                {/* Empty chat */}
                {(!Array.isArray(chat) || chat.length === 0) && (
                    <div className="text-center text-gray-400 py-6">No messages yet</div>
                )}
            </div>

            {/* Input area */}
            <div className="p-4 bg-white border-t">
                <div className="flex space-x-2">
                    <input
                        type="text"
                        value={msg}
                        onChange={(e) => setMsg(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none appearance-none"
                    />
                    <button
                        onClick={() => {
                            if (msg.trim().length > 0) {
                                send.mutate();
                                setMsg("");
                            }
                        }}
                        className="w-12 h-12 bg-cyan-500 text-white rounded-full flex items-center justify-center"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* ✅ Fullscreen image modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[999]"
                    onClick={() => setSelectedImage(null)}
                >
                    <div
                        className="relative"
                        onClick={(e) => e.stopPropagation()} // prevent closing when clicking image
                    >
                        <button
                            className="absolute top-2 right-2 bg-white/80 text-gray-700 rounded-full p-2"
                            onClick={() => setSelectedImage(null)}
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <img
                            src={selectedImage}
                            alt="Full view"
                            className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-lg"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chat;
